// ** Custom Hooks
import { useNavigationData } from "../../hooks/use-navigation-data";

// ** Gatsby
import { Link, navigate } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

interface FooterProps {
  partnerLogo?: string;
}

// ** Custom Components
import Box from "../Box";
import Container from "../Container";
import Text from "../Text";
import { PortableText } from "@portabletext/react";
import { serializers } from "../Serializers";
import { useContext } from "react";
import PageContext from "../../contexts/PageContext";
import { parseLink } from "../../utils/helpers";
import i18n from "../../data/i18n_constants";

function Footer(props: FooterProps) {
  const { partnerLogo } = props;
  const page = useContext(PageContext);

  const lang = page.i18n_lang;

  const isEn = lang === "en_US";
  const isFr = lang === "fr_CA";

  const {
    footer: { links },
    footerCopy,
  } = useNavigationData();

  const internalLinks = links.filter(
    (link: any) => link._type === "linkInternal"
  );
  const externalLinks = links.filter(
    (link: any) => link._type === "linkExternal"
  );

  const logoLink = isFr ? "/8Twelve_Mortgage_fr.svg" : "/8Twelve_Mortgage.svg";

  return (
    <Box
      as="footer"
      className="pt-16 lg:pt-32 mt-16 lg:mt-32 bg-[#B9B8BC] bg-opacity-20 pb-6 lg:pb-16"
    >
      <Container constrainedToBreakPoint className="px-4">
        <Box className="grid justify-items-center grid-cols-1 md:grid-cols-[repeat(3,minmax(auto,_100%))]  mb-16">
          <Box className="mb-[50px] md:mr-auto">
            <img
              className="h-[98px] w-[288px] cursor-pointer"
              src={partnerLogo ? partnerLogo : logoLink}
              alt="8Twelve Mortgage"
              aria-label="8Twelve Mortgage"
              onClick={() => navigate("/")}
            />
          </Box>
          <Box className="mb-10">
            <a
              href="tel:1-877-812-8123"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[30px] font-bold py-1 md:py-0 px-8 md:px-0 rounded-[25.5px] text-white md:text-blue-500 bg-orange-500 md:bg-transparent"
            >
              1.877.812.8123
            </a>
          </Box>
          <Box className="flex gap-x-2">
            <a
              href="https://www.facebook.com/8twelvemortgage"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StaticImage
                objectFit="contain"
                className="h-[40px] w-[40px] md:h-[25px] md:w-[25px] cursor-pointer"
                placeholder="blurred"
                src="../../images/logo-facebook.png"
                alt="8Twelve Mortgage"
                quality={100}
              />
            </a>
            <a
              href="https://twitter.com/8twelvemortgage"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StaticImage
                objectFit="contain"
                className="h-[40px] w-[40px] md:h-[25px] md:w-[25px] cursor-pointer"
                placeholder="blurred"
                src="../../images/logo-twitter.png"
                alt="8Twelve Mortgage"
                quality={100}
              />
            </a>
            <a
              href="https://www.youtube.com/channel/UCUtJ6bzSGzG0UFr-sW1U6Gw"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StaticImage
                objectFit="contain"
                className="h-[40px] w-[40px] md:h-[25px] md:w-[25px] cursor-pointer"
                placeholder="blurred"
                src="../../images/logo-youtube.png"
                alt="8Twelve Mortgage"
                quality={100}
              />
            </a>
            <a
              href="https://www.linkedin.com/company/8twelve-mortgage/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StaticImage
                objectFit="contain"
                className="h-[40px] w-[40px] md:h-[25px] md:w-[25px] cursor-pointer"
                placeholder="blurred"
                src="../../images/logo-linkedin.png"
                alt="8Twelve Mortgage"
                quality={100}
              />
            </a>
            <a
              href="https://www.instagram.com/8twelvemortgage/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StaticImage
                objectFit="contain"
                className="h-[40px] w-[40px] md:h-[25px] md:w-[25px] cursor-pointer"
                placeholder="blurred"
                src="../../images/logo-instagram-new.png"
                alt="8Twelve Mortgage"
                quality={100}
              />
            </a>
          </Box>
        </Box>
        <Box className="grid max-w-xs md:max-w-[100vw] mx-auto grid-cols-1 md:grid-cols-4 gap-4">
          <Box className="hidden md:block">
            <Box as="ul" className="flex flex-col gap-y-2">
              {internalLinks?.slice(0, 9)?.map((link: any, idx: any) => (
                <Box as="li" key={idx}>
                  <Link
                    className="text-sm"
                    to={isEn ? parseLink(link) : parseLink(link.i18n_refs[0])}
                  >
                    {isEn ? link.title : link.i18n_refs[0]?.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>
          <Box className="hidden md:block">
            <Box as="ul" className="flex flex-col gap-y-2">
              {internalLinks?.slice(9)?.map((link: any, idx: any) => (
                <Box as="li" key={idx}>
                  <Link
                    to={isEn ? parseLink(link) : parseLink(link.i18n_refs[0])}
                    className="text-sm"
                  >
                    {isEn ? link.title : link.i18n_refs[0]?.title}
                  </Link>
                </Box>
              ))}
              <Box as="div" className="border-b-[0.25px] border-gray-700" />
              {externalLinks?.slice(0, 1)?.map((link: any, idx: any) => (
                <Box as="li" key={idx}>
                  <a
                    href={`${link.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm"
                  >
                    <b>{isEn ? link.title : link.i18n_refs[0]?.title}</b>
                  </a>
                </Box>
              ))}
            </Box>
          </Box>
          {externalLinks[1] && (
            <Box className="flex flex-col items-center md:items-start">
              <Text className="font-bold mb-2 !text-sm">
                {isEn
                  ? externalLinks[1].title
                  : externalLinks[1].i18n_refs[0]?.title}
              </Text>
              <Box className="[&>p]:max-md:text-center [&>a]:md:text-left [&>p>a]:font-normal [&>p>a]:text-gray-700 [&>p]:text-sm">
                <PortableText
                  value={externalLinks[1]._rawContent}
                  components={serializers}
                />
              </Box>
            </Box>
          )}

          <Box className="flex flex-col items-center md:items-start gap-y-2">
            {externalLinks?.slice(2)?.map((link: any, idx: any) => (
              <Box
                key={idx}
                className="flex flex-col items-center md:items-start"
              >
                <Text className="font-bold mb-2 !text-sm">
                  {isEn ? link.title : link.i18n_refs[0]?.title}
                </Text>
                <Box className="[&>p]:max-md:text-center [&>a]:md:text-left [&>p>a]:font-normal [&>p>a]:text-gray-700 [&>p]:text-sm mb-4">
                  <PortableText
                    value={link._rawContent}
                    components={serializers}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          as="div"
          className="border-b-[0.25px] border-gray-700 mt-6 lg:mt-16"
        />
        <Box className="flex items-center flex-col gap-2 mt-2">
          <Box className="text-[12px] md:text-[14px] text-gray-700 text-center">
            {isEn ? footerCopy.en_title : footerCopy.fr_title}
          </Box>
          <Box className="text-[12px] md:text-[14px] text-gray-700">
            Copyright &copy; {new Date().getFullYear()}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
